import { useCallback, useEffect, useState } from 'react';

/**
 * 可用主题类型
 * 对应 tailwind.config.ts 中定义的主题
 */
export type Theme = 'dark' | 'light';

const THEME_STORAGE_KEY = 'app-theme';
const AVAILABLE_THEMES: Theme[] = ['dark', 'light'];

/**
 * 主题切换 Hook (tailwindcss-themer 版本)
 * 
 * tailwindcss-themer 通过在 html 元素上添加主题名作为 class 来切换主题
 * - 默认主题 (light): 无需添加任何 class
 * - 其他主题: 添加主题名作为 class，如 <html class="dark">
 * 
 * @example
 * ```tsx
 * const { theme, setTheme, toggleTheme, availableThemes } = useTheme();
 * 
 * return (
 *   <select value={theme} onChange={e => setTheme(e.target.value as Theme)}>
 *     {availableThemes.map(t => (
 *       <option key={t} value={t}>{t}</option>
 *     ))}
 *   </select>
 * );
 * ```
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    // 1. 优先从 localStorage 读取
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme;
    if (AVAILABLE_THEMES.includes(stored)) {
      return stored;
    }
    
    // 2. 检测系统偏好
    // if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    //   return 'dark';
    // }
    
    // 3. 默认 light
    return 'light';
  });

  // 应用主题到 DOM
  const applyTheme = useCallback((newTheme: Theme) => {
    const root = document.documentElement;
    
    // 移除所有主题 class
    AVAILABLE_THEMES.forEach(t => {
      if (t !== 'light') { // light 是默认主题，不需要 class
        root.classList.remove(t);
      }
    });
    
    // 添加新主题 class（light 除外，它是默认主题）
    if (newTheme !== 'light') {
      root.classList.add(newTheme);
    }
  }, []);

  // 设置主题
  const setTheme = useCallback((newTheme: Theme) => {
    if (!AVAILABLE_THEMES.includes(newTheme)) {
      console.warn(`Unknown theme: ${newTheme}. Available themes: ${AVAILABLE_THEMES.join(', ')}`);
      return;
    }
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    applyTheme(newTheme);
  }, [applyTheme]);

  // 切换到下一个主题
  const toggleTheme = useCallback(() => {
    const currentIndex = AVAILABLE_THEMES.indexOf(theme);
    const nextIndex = (currentIndex + 1) % AVAILABLE_THEMES.length;
    setTheme(AVAILABLE_THEMES[nextIndex]);
  }, [theme, setTheme]);

  // 在 dark 和 light 之间快速切换
  const toggleDarkLight = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  // 初始化时应用主题
  useEffect(() => {
    applyTheme(theme);
  }, []);

  // 监听系统主题变化
  // useEffect(() => {
  //   const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    
  //   const handleChange = (e: MediaQueryListEvent) => {
  //     // 如果用户没有手动设置过主题，跟随系统
  //     if (!localStorage.getItem(THEME_STORAGE_KEY)) {
  //       setTheme(e.matches ? 'light' : 'dark');
  //     }
  //   };

  //   mediaQuery.addEventListener('change', handleChange);
  //   return () => mediaQuery.removeEventListener('change', handleChange);
  // }, [setTheme]);

  return {
    theme,
    setTheme,
    toggleTheme,
    toggleDarkLight,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    availableThemes: AVAILABLE_THEMES,
  };
}
